import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { LogOut, BookOpen, Award, Settings, Clock, CheckCircle, Lock, CreditCard, PlayCircle, Eye, Download, X, MessageSquare, FolderOpen, User, Save } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import LoadingQuip from "@/components/LoadingQuip";
import { getDisplayName } from "@/utils/displayName";
import DarkModeToggle from "@/components/DarkModeToggle";
import { useTheme } from "@/contexts/ThemeContext";
import { Label } from "@/components/ui/label";
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

function CertificatePdfViewer({ fileData }) {
  const [numPages, setNumPages] = useState(null);

  return (
    <Document
      file={fileData}
      onLoadSuccess={({ numPages }) => setNumPages(numPages)}
      onLoadError={(error) => console.error('PDF load error:', error)}
      loading={<p className="text-gray-500 p-4">Loading PDF...</p>}
      error={<p className="text-red-500 p-4">Failed to render certificate. Please use the Download button.</p>}
    >
      {numPages && Array.from({ length: numPages }, (_, i) => (
        <Page key={i + 1} pageNumber={i + 1} width={700} className="mb-4 shadow-lg" />
      ))}
    </Document>
  );
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_11207ce1-f0ad-4af8-83b6-64fb331f35a6/artifacts/e6s5tkbt_OSSuccess6.png";

// US States list
const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

export default function StudentDashboard({ user, logout, setUser }) {
  const [courses, setCourses] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(null);
  const [viewingCertificate, setViewingCertificate] = useState(null);
  const [certificatePdfUrl, setCertificatePdfUrl] = useState(null);
  const [loadingCertificate, setLoadingCertificate] = useState(false);
  const [activeTab, setActiveTab] = useState('in-progress');
  const [certificateYearFilter, setCertificateYearFilter] = useState('all');
  const [certificateSortBy, setCertificateSortBy] = useState('newest');
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [profileForm, setProfileForm] = useState({
    state: user?.state || '',
    license_number: user?.license_number || '',
    agd_number: user?.agd_number || ''
  });
  const [savingProfile, setSavingProfile] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { isDarkMode } = useTheme();
  
  const token = localStorage.getItem("session_token");
  const headers = { Authorization: `Bearer ${token}` };

  // Theme-aware colors
  const colors = {
    heading: isDarkMode ? '#ffffff' : '#092036',
    subtext: isDarkMode ? '#e2e8f0' : '#70767C',
    cardBg: isDarkMode ? '#0f2744' : 'white',
    tableBg: isDarkMode ? '#0f2744' : 'white',
    tableHeaderBg: isDarkMode ? '#1e3a5f' : '#F4F4F4',
    tableRowHover: isDarkMode ? '#1e3a5f' : '#f9fafb',
    border: isDarkMode ? '#1e3a5f' : '#DADEEO',
    tabInactive: isDarkMode ? '#94a3b8' : '#70767C',
    tabActiveBg: isDarkMode ? 'transparent' : 'transparent',
  };

  // Check if user needs to complete profile (missing state/license for certificates)
  const needsProfileUpdate = !user?.state || !user?.license_number;

  // Save profile updates
  const handleSaveProfile = async () => {
    if (!profileForm.state || !profileForm.license_number) {
      toast.error('Please fill in both State and License Number');
      return;
    }
    
    setSavingProfile(true);
    try {
      const formData = new FormData();
      formData.append('state', profileForm.state);
      formData.append('license_number', profileForm.license_number);
      formData.append('agd_number', profileForm.agd_number || '');
      
      const response = await axios.put(`${API}/users/profile`, formData, { headers });
      
      if (response.data) {
        // Update local user state
        if (setUser) {
          setUser(prev => ({
            ...prev,
            state: profileForm.state,
            license_number: profileForm.license_number,
            agd_number: profileForm.agd_number || null
          }));
        }
        toast.success('Profile updated successfully! Your certificates will now include your license information.');
        setShowProfileModal(false);
      }
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error(error.response?.data?.detail || 'Failed to save profile');
    } finally {
      setSavingProfile(false);
    }
  };

  useEffect(() => {
    fetchCourses();
    fetchCertificates();
    fetchSubscriptionStatus();
    
    // Check if returning from Stripe
    const searchParams = new URLSearchParams(location.search);
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      checkPaymentStatus(sessionId);
    }
    
    // Check if coming from email link for Curbside Consult
    const tab = searchParams.get('tab');
    const questionId = searchParams.get('question');
    if (tab === 'curbside' && questionId) {
      // Set tab and redirect to Curbside Consult with question parameter
      setActiveTab('curbside-consult');
      // Small delay to ensure tab is set, then navigate
      setTimeout(() => {
        navigate(`/curbside-consult?question=${questionId}`);
      }, 100);
    }
  }, [location]);

  // Smart default tab logic
  useEffect(() => {
    if (courses.length === 0) return;
    
    const inProgressCourses = courses.filter(c => c.status === 'in_progress');
    const completedCourses = courses.filter(c => c.status === 'completed');
    
    // If has in-progress courses, show them by default
    if (inProgressCourses.length > 0) {
      setActiveTab('in-progress');
    } 
    // If no in-progress but has completed, show available courses
    else if (completedCourses.length > 0) {
      setActiveTab('available');
    }
    // Brand new student, show available courses
    else {
      setActiveTab('available');
    }
  }, [courses]);

  const fetchCourses = async () => {
    try {
      const response = await axios.get(`${API}/courses`, { headers });
      setCourses(response.data);
      setLoading(false);
    } catch (error) {
      toast.error("Error loading courses");
      setLoading(false);
    }
  };

  const fetchCertificates = async () => {
    try {
      const response = await axios.get(`${API}/my-certificates`, { headers });
      setCertificates(response.data);
    } catch (error) {
      console.error("Error loading certificates:", error);
    }
  };

  const fetchSubscriptionStatus = async () => {
    try {
      const response = await axios.get(`${API}/subscriptions/status`, { headers });
      setSubscriptionStatus(response.data);
    } catch (error) {
      console.error("Error loading subscription status:", error);
    }
  };

  // Check if user has Curbside Consult access
  const hasCurbsideAccess = subscriptionStatus?.has_curbside_access || 
    subscriptionStatus?.active_plans?.includes('curbside-consult') ||
    user?.has_curbside_access;

  const handleViewCertificate = async (courseId) => {
    setViewingCertificate(courseId);
    setLoadingCertificate(true);
    
    try {
      const response = await fetch(`${API}/courses/${courseId}/certificate/download`, {
        headers: { 'Authorization': 'Bearer ' + token }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        // Convert to base64 data URL to avoid ad blocker blocking blob URLs
        const reader = new FileReader();
        reader.onloadend = () => {
          setCertificatePdfUrl(reader.result); // data:application/pdf;base64,...
          setLoadingCertificate(false);
        };
        reader.readAsDataURL(blob);
      } else {
        toast.error('Certificate not available. The course may have been removed.');
        setViewingCertificate(null);
        setLoadingCertificate(false);
      }
    } catch (error) {
      console.error('Error loading certificate:', error);
      toast.error('Failed to load certificate. Please try again.');
      setViewingCertificate(null);
      setLoadingCertificate(false);
    }
  };
  
  const handleCloseCertificate = () => {
    setCertificatePdfUrl(null);
    setViewingCertificate(null);
  };

  const handleDownloadCertificate = async (courseId, courseTitle) => {
    try {
      const response = await fetch(`${API}/courses/${courseId}/certificate/download`, {
        headers: { 'Authorization': 'Bearer ' + token }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = `Certificate_${(courseTitle || 'Course').replace(/\s+/g, '_')}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
        toast.success('Certificate downloaded!');
      } else {
        toast.error('Certificate not available. The course may have been removed.');
      }
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download certificate. Please try again.');
    }
  };

  const handlePurchaseCourse = async (courseId) => {
    setProcessingPayment(courseId);
    
    try {
      // Get origin URL from window
      const originUrl = window.location.origin;
      
      // Create form data
      const formData = new FormData();
      formData.append('course_id', courseId);
      formData.append('origin_url', originUrl);
      
      // Create checkout session
      const response = await axios.post(`${API}/create-checkout-session`, formData, { headers });
      
      // Redirect to Stripe checkout
      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        toast.error('Failed to create payment session');
        setProcessingPayment(null);
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.response?.data?.detail || 'Failed to initiate payment');
      setProcessingPayment(null);
    }
  };

  const checkPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000; // 2 seconds
    
    if (attempts >= maxAttempts) {
      toast.error('Payment verification timed out. Please refresh the page.');
      // Clean up URL
      window.history.replaceState({}, document.title, '/dashboard');
      return;
    }
    
    try {
      const response = await axios.get(`${API}/checkout-status/${sessionId}`, { headers });
      const data = response.data;
      
      if (data.payment_status === 'paid') {
        toast.success('Payment successful! You now have access to the course.');
        // Refresh courses list
        await fetchCourses();
        // Clean up URL
        window.history.replaceState({}, document.title, '/dashboard');
        return;
      } else if (data.status === 'expired') {
        toast.error('Payment session expired. Please try again.');
        window.history.replaceState({}, document.title, '/dashboard');
        return;
      }
      
      // If payment is still pending, continue polling
      setTimeout(() => checkPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      toast.error('Error verifying payment. Please contact support.');
      window.history.replaceState({}, document.title, '/dashboard');
    }
  };

  // Export CE report as CSV
  // Generate PDF CE report
  const generatePDFReport = async (filteredCerts) => {
    try {
      // Create a simple HTML report
      const totalCE = filteredCerts.reduce((sum, cert) => {
        const course = courses.find(c => c.id === cert.course_id);
        return sum + (course?.ce_hours || 0);
      }, 0);

      const reportHTML = `
        <!DOCTYPE html>
        <html>
        <head>
          <style>
            body { font-family: Arial, sans-serif; padding: 40px; }
            h1 { color: #092036; border-bottom: 3px solid #102862; padding-bottom: 10px; }
            .header { margin-bottom: 30px; }
            .summary { background: #f0fdf4; padding: 15px; margin: 20px 0; border-left: 4px solid #16a34a; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th { background: #102862; color: white; padding: 12px; text-align: left; }
            td { padding: 10px; border-bottom: 1px solid #ddd; }
            tr:hover { background: #f9fafb; }
            .footer { margin-top: 40px; font-size: 12px; color: #666; }
            .action-buttons { 
              position: fixed; 
              top: 20px; 
              right: 20px; 
              display: flex; 
              gap: 10px; 
              z-index: 1000; 
            }
            .btn { 
              padding: 10px 20px; 
              border: none; 
              border-radius: 6px; 
              cursor: pointer; 
              font-size: 14px; 
              font-weight: 600;
              transition: opacity 0.2s;
            }
            .btn:hover { opacity: 0.8; }
            .btn-close { background: #ef4444; color: white; }
            .btn-print { background: #102862; color: white; }
            @media print {
              .action-buttons { display: none; }
            }
          </style>
        </head>
        <body>
          <div class="action-buttons">
            <button class="btn btn-print" onclick="window.print()">Print Report</button>
            <button class="btn btn-close" onclick="window.close()">Close</button>
          </div>
          <h1>Continuing Education Certificate Report</h1>
          <div class="header">
            <p><strong>Dentist:</strong> ${getDisplayName(user)}</p>
            <p><strong>Email:</strong> ${user.email}</p>
            <p><strong>Report Date:</strong> ${new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
          </div>
          <div class="summary">
            <strong>Total CE Hours Earned:</strong> ${totalCE}
          </div>
          <table>
            <thead>
              <tr>
                <th>Course</th>
                <th>CE Hours</th>
                <th>Completion Date</th>
              </tr>
            </thead>
            <tbody>
              ${filteredCerts.map(cert => {
                const course = courses.find(c => c.id === cert.course_id);
                const ceHours = course?.ce_hours || 0;
                const date = new Date(cert.issued_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                return `
                  <tr>
                    <td>${cert.course_title}</td>
                    <td>${ceHours}</td>
                    <td>${date}</td>
                  </tr>
                `;
              }).join('')}
            </tbody>
          </table>
          <div class="footer">
            <p>This report was generated from Oral Surgery Success CE platform.</p>
          </div>
        </body>
        </html>
      `;

      // Open print dialog with the report
      const printWindow = window.open('', '_blank');
      printWindow.document.write(reportHTML);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
      }, 250);
      toast.success('Opening CE report for printing...');
    } catch (error) {
      console.error('Error generating PDF report:', error);
      toast.error('Error generating report');
    }
  };

  return (
    <div className="min-h-screen overflow-x-hidden grid-layout-page" style={{ background: isDarkMode ? '#092036' : '#F4F4F4' }}>
      {/* Header */}
      <header className={`sticky top-0 z-50 ${isDarkMode ? '' : 'bg-white/90 backdrop-blur-md border-b'}`} style={{ backgroundColor: isDarkMode ? '#092036' : undefined, borderColor: isDarkMode ? 'transparent' : '#DADEEO' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
            <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1 cursor-pointer" onClick={() => navigate("/")}>
              <img src={LOGO_URL} alt="Oral Surgery Success" className="h-8 w-8 sm:h-10 sm:w-10 object-contain flex-shrink-0" style={isDarkMode ? { filter: 'drop-shadow(0 0 2px rgba(255,255,255,0.8))' } : {}} />
              <div className="min-w-0 flex-1">
                <h1 className="text-base sm:text-xl font-bold truncate" style={{ color: colors.heading }}>
                  Welcome back, {getDisplayName(user)}
                </h1>
                <p className="text-xs sm:text-sm truncate" style={{ color: colors.subtext }}>
                  {user.email}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto">
              <DarkModeToggle />
              {user.role === "admin" && (
                <Button 
                  data-testid="back-to-admin-btn"
                  onClick={() => navigate("/admin")}
                  variant="outline"
                  size="sm"
                  className="gap-1 sm:gap-2 flex-1 sm:flex-initial"
                  style={{ borderColor: '#9E3639', color: '#9E3639' }}
                >
                  <Settings className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="hidden sm:inline">Course Creator</span>
                  <span className="sm:hidden">Admin</span>
                </Button>
              )}
              <Button 
                onClick={() => setShowProfileModal(true)}
                variant="outline"
                size="sm"
                className="gap-1 sm:gap-2"
                style={needsProfileUpdate ? { borderColor: '#f59e0b', color: '#f59e0b' } : {}}
              >
                <User className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">Profile</span>
                {needsProfileUpdate && <span className="w-2 h-2 bg-orange-500 rounded-full" />}
              </Button>
              <Button 
                data-testid="student-logout-btn"
                onClick={logout}
                variant="outline"
                size="sm"
                className="gap-1 sm:gap-2"
              >
                <LogOut className="w-3 h-3 sm:w-4 sm:h-4" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 pb-6 sm:pb-8 sm:pt-8">
        {/* My CE Status Strip */}
        {!loading && courses.length > 0 && (
          <Card className="mb-6 p-4 sm:p-6" style={{ background: '#eff6ff', border: '1px solid #bfdbfe' }}>
            <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3 sm:gap-2 text-sm">
              <span className="font-semibold sm:mr-2" style={{ color: '#092036' }}>My CE status</span>
              <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-2 sm:gap-2">
                <span style={{ color: '#70767C' }}>
                  Total CE hours earned: <span className="font-semibold" style={{ color: '#092036' }}>{(() => {
                    const completedCourses = courses.filter(c => c.status === 'completed');
                    return completedCourses.reduce((sum, course) => sum + (course.ce_hours || 0), 0);
                  })()}</span>
                </span>
                <span className="hidden sm:inline" style={{ color: '#70767C' }}>·</span>
                <span style={{ color: '#70767C' }}>
                  Courses in progress: <span className="font-semibold" style={{ color: '#092036' }}>{courses.filter(c => c.status === 'in_progress').length}</span>
                </span>
                <span className="hidden sm:inline" style={{ color: '#70767C' }}>·</span>
                <span style={{ color: '#70767C' }}>
                  Completed courses: <span className="font-semibold" style={{ color: '#092036' }}>{courses.filter(c => c.status === 'completed').length}</span>
                </span>
              </div>
            </div>
          </Card>
        )}

        {/* Certificates Available Banner */}
        {!loading && certificates.length > 0 && (
          <Card 
            className="mb-6 p-4 sm:p-5 cursor-pointer hover:shadow-md transition-shadow" 
            style={{ background: isDarkMode ? '#1a2e44' : '#fffbeb', border: isDarkMode ? '1px solid #854d0e' : '1px solid #fbbf24' }}
            onClick={() => setActiveTab('certificates')}
            data-testid="certificates-banner"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: isDarkMode ? '#854d0e' : '#fef3c7' }}>
                  <Award className="w-5 h-5" style={{ color: '#f59e0b' }} />
                </div>
                <div>
                  <p className="font-semibold" style={{ color: isDarkMode ? '#fbbf24' : '#92400e' }}>
                    You have {certificates.length} {certificates.length === 1 ? 'certificate' : 'certificates'} ready to download
                  </p>
                  <p className="text-sm" style={{ color: isDarkMode ? '#fcd34d' : '#b45309' }}>
                    Tap here to view and download your CE certificates
                  </p>
                </div>
              </div>
              <Download className="w-5 h-5 flex-shrink-0" style={{ color: '#f59e0b' }} />
            </div>
          </Card>
        )}

        {/* Next Step Recommendation Card */}
        {!loading && courses.length > 0 && (() => {
          const inProgressCourses = courses.filter(c => c.status === 'in_progress');
          const completedCourses = courses.filter(c => c.status === 'completed');
          const availableCourses = courses.filter(c => c.status === 'not_started' && c.has_access);
          
          // Case A: Has in-progress courses
          if (inProgressCourses.length > 0) {
            // Find most recently accessed (for now, just use first one)
            const currentCourse = inProgressCourses[0];
            
            return (
              <Card className="mb-6 p-6" style={{ 
                background: isDarkMode 
                  ? 'linear-gradient(135deg, #1e3a5f 0%, #0f2744 100%)' 
                  : 'linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%)', 
                border: isDarkMode ? '2px solid #60a5fa' : '2px solid #0ea5e9' 
              }}>
                <div className="mb-2">
                  <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: isDarkMode ? '#60a5fa' : '#0369a1' }}>Next step</span>
                </div>
                <h3 className="text-2xl font-bold mb-3" style={{ color: isDarkMode ? '#ffffff' : '#092036' }}>Pick up where you left off</h3>
                <div className="mb-4">
                  <h4 className="text-lg font-semibold mb-1" style={{ color: isDarkMode ? '#e0f2fe' : '#102862' }}>{currentCourse.title}</h4>
                  <p className="text-sm mb-2" style={{ color: isDarkMode ? '#94a3b8' : '#70767C' }}>
                    {currentCourse.description?.substring(0, 120)}...
                  </p>
                  {currentCourse.progress && (
                    <div className="flex items-center gap-2 text-xs mt-2" style={{ color: isDarkMode ? '#94a3b8' : '#70767C' }}>
                      <Clock className="w-4 h-4" />
                      <span>Time spent: {currentCourse.progress.time_spent_minutes} mins · Questions asked: {currentCourse.progress.questions_asked}</span>
                    </div>
                  )}
                </div>
                <div className="flex gap-3 items-center flex-wrap">
                  <Button
                    onClick={() => navigate(`/course/${currentCourse.id}`)}
                    className="gap-2 text-white"
                    style={{ background: isDarkMode ? '#3b82f6' : '#102862' }}
                  >
                    <PlayCircle className="w-4 h-4" />
                    Resume course
                  </Button>
                  <button
                    onClick={() => setActiveTab('in-progress')}
                    className="text-sm font-medium hover:underline"
                    style={{ color: isDarkMode ? '#60a5fa' : '#102862' }}
                  >
                    View all in-progress courses →
                  </button>
                </div>
              </Card>
            );
          }
          
          // Case B: No in-progress but has completed courses
          if (completedCourses.length > 0 && availableCourses.length > 0) {
            // Recommend the first available course
            const recommendedCourse = availableCourses[0];
            
            return (
              <Card className="mb-6 p-6" style={{ 
                background: isDarkMode 
                  ? 'linear-gradient(135deg, #14532d 0%, #0f2744 100%)' 
                  : 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)', 
                border: isDarkMode ? '2px solid #22c55e' : '2px solid #16a34a' 
              }}>
                <div className="mb-2">
                  <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: isDarkMode ? '#22c55e' : '#15803d' }}>Next step</span>
                </div>
                <h3 className="text-2xl font-bold mb-3" style={{ color: isDarkMode ? '#ffffff' : '#092036' }}>Start your next course</h3>
                <div className="mb-4">
                  <h4 className="text-lg font-semibold mb-1" style={{ color: isDarkMode ? '#bbf7d0' : '#102862' }}>{recommendedCourse.title}</h4>
                  <p className="text-sm mb-2" style={{ color: isDarkMode ? '#94a3b8' : '#70767C' }}>
                    {recommendedCourse.description?.substring(0, 120)}...
                  </p>
                  <div className="flex items-center gap-3 text-xs mt-2" style={{ color: isDarkMode ? '#94a3b8' : '#70767C' }}>
                    <div className="flex items-center gap-1">
                      <Award className="w-4 h-4" style={{ color: isDarkMode ? '#22c55e' : '#16a34a' }} />
                      <span>{recommendedCourse.ce_hours || 1} CE hour{recommendedCourse.ce_hours !== 1 ? 's' : ''}</span>
                    </div>
                    {recommendedCourse.min_time_minutes && (
                      <>
                        <span>·</span>
                        <div className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          <span>~{recommendedCourse.min_time_minutes} minutes</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
                <Button
                  onClick={() => navigate(`/course/${recommendedCourse.id}`)}
                  className="gap-2 text-white"
                  style={{ background: '#16a34a' }}
                >
                  <BookOpen className="w-4 h-4" />
                  Start course
                </Button>
              </Card>
            );
          }
          
          // Case C: Brand new user
          if (completedCourses.length === 0 && inProgressCourses.length === 0 && availableCourses.length > 0) {
            // Use the first available course as intro course
            const introCourse = availableCourses[0];
            
            return (
              <Card className="mb-6 p-6" style={{ 
                background: isDarkMode 
                  ? 'linear-gradient(135deg, #065f46 0%, #0f2744 100%)' 
                  : 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)', 
                border: isDarkMode ? '2px solid #10b981' : '2px solid #10b981' 
              }}>
                <div className="mb-2">
                  <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: isDarkMode ? '#34d399' : '#059669' }}>Next step</span>
                </div>
                <h3 className="text-2xl font-bold mb-3" style={{ color: isDarkMode ? '#ffffff' : '#092036' }}>Start here</h3>
                <div className="mb-4">
                  <h4 className="text-lg font-semibold mb-1" style={{ color: isDarkMode ? '#a7f3d0' : '#102862' }}>{introCourse.title}</h4>
                  <p className="text-sm mb-2" style={{ color: isDarkMode ? '#94a3b8' : '#70767C' }}>
                    This is a great course to begin your continuing education journey. Build your foundational knowledge and earn CE hours.
                  </p>
                  <div className="flex items-center gap-3 text-xs mt-2" style={{ color: isDarkMode ? '#94a3b8' : '#70767C' }}>
                    <div className="flex items-center gap-1">
                      <Award className="w-4 h-4" style={{ color: isDarkMode ? '#34d399' : '#10b981' }} />
                      <span>{introCourse.ce_hours || 1} CE hour{introCourse.ce_hours !== 1 ? 's' : ''}</span>
                    </div>
                    {introCourse.min_time_minutes && (
                      <>
                        <span>·</span>
                        <div className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          <span>~{introCourse.min_time_minutes} minutes</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
                <Button
                  onClick={() => navigate(`/course/${introCourse.id}`)}
                  className="gap-2 text-white"
                  style={{ background: '#10b981' }}
                >
                  <BookOpen className="w-4 h-4" />
                  Begin your first course
                </Button>
              </Card>
            );
          }
          
          return null;
        })()}

        {/* Tab Navigation */}
        <div className="mb-8 overflow-x-auto">
          <div className="flex flex-col sm:flex-row gap-1 border-b-2 sm:border-b-2 min-w-max" style={{ borderColor: colors.border }}>
            <button
              onClick={() => setActiveTab('in-progress')}
              className={`px-4 sm:px-4 py-3 font-semibold transition-all text-left sm:text-center whitespace-nowrap ${
                activeTab === 'in-progress'
                  ? 'border-l-4 sm:border-l-0 sm:border-b-4 sm:-mb-0.5'
                  : 'opacity-70 hover:opacity-100'
              }`}
              style={{
                color: activeTab === 'in-progress' ? (isDarkMode ? '#60a5fa' : '#102862') : colors.tabInactive,
                borderColor: activeTab === 'in-progress' ? (isDarkMode ? '#60a5fa' : '#102862') : 'transparent'
              }}
            >
              <div className="flex items-center gap-1.5">
                <PlayCircle className="w-4 h-4" />
                <span className="text-sm">In Progress</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('available')}
              className={`px-4 sm:px-4 py-3 font-semibold transition-all text-left sm:text-center whitespace-nowrap ${
                activeTab === 'available'
                  ? 'border-l-4 sm:border-l-0 sm:border-b-4 sm:-mb-0.5'
                  : 'opacity-70 hover:opacity-100'
              }`}
              style={{
                color: activeTab === 'available' ? (isDarkMode ? '#60a5fa' : '#102862') : colors.tabInactive,
                borderColor: activeTab === 'available' ? (isDarkMode ? '#60a5fa' : '#102862') : 'transparent'
              }}
            >
              <div className="flex items-center gap-1.5">
                <BookOpen className="w-4 h-4" />
                <span className="text-sm">Available</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('completed')}
              className={`px-4 sm:px-4 py-3 font-semibold transition-all text-left sm:text-center whitespace-nowrap ${
                activeTab === 'completed'
                  ? 'border-l-4 sm:border-l-0 sm:border-b-4 sm:-mb-0.5'
                  : 'opacity-70 hover:opacity-100'
              }`}
              style={{
                color: activeTab === 'completed' ? (isDarkMode ? '#60a5fa' : '#102862') : colors.tabInactive,
                borderColor: activeTab === 'completed' ? (isDarkMode ? '#60a5fa' : '#102862') : 'transparent'
              }}
            >
              <div className="flex items-center gap-1.5">
                <CheckCircle className="w-4 h-4" />
                <span className="text-sm">Completed</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('certificates')}
              className={`px-4 sm:px-4 py-3 font-semibold transition-all text-left sm:text-center whitespace-nowrap ${
                activeTab === 'certificates'
                  ? 'border-l-4 sm:border-l-0 sm:border-b-4 sm:-mb-0.5'
                  : 'opacity-70 hover:opacity-100'
              }`}
              style={{
                color: activeTab === 'certificates' ? (isDarkMode ? '#60a5fa' : '#102862') : colors.tabInactive,
                borderColor: activeTab === 'certificates' ? (isDarkMode ? '#60a5fa' : '#102862') : 'transparent'
              }}
            >
              <div className="flex items-center gap-1.5">
                <Award className="w-4 h-4" />
                <span className="text-sm">Certificates</span>
                {certificates.length > 0 && (
                  <span className="ml-1 px-1.5 py-0.5 text-xs font-bold rounded-full bg-amber-100 text-amber-700">{certificates.length}</span>
                )}
              </div>
            </button>
            <button
              onClick={() => setActiveTab('practice-resources')}
              className={`px-4 sm:px-4 py-3 font-semibold transition-all text-left sm:text-center whitespace-nowrap ${
                activeTab === 'practice-resources'
                  ? 'border-l-4 sm:border-l-0 sm:border-b-4 sm:-mb-0.5'
                  : 'opacity-70 hover:opacity-100'
              }`}
              style={{
                color: activeTab === 'practice-resources' ? '#16a34a' : colors.tabInactive,
                borderColor: activeTab === 'practice-resources' ? '#16a34a' : 'transparent'
              }}
            >
              <div className="flex items-center gap-1.5">
                <FolderOpen className="w-4 h-4" />
                <span className="text-sm">Resources</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('curbside-consult')}
              className={`px-4 sm:px-4 py-3 font-semibold transition-all text-left sm:text-center whitespace-nowrap ${
                activeTab === 'curbside-consult'
                  ? 'border-l-4 sm:border-l-0 sm:border-b-4 sm:-mb-0.5'
                  : 'opacity-70 hover:opacity-100'
              }`}
              style={{
                color: activeTab === 'curbside-consult' ? '#f97316' : colors.tabInactive,
                borderColor: activeTab === 'curbside-consult' ? '#f97316' : 'transparent'
              }}
            >
              <div className="flex items-center gap-1.5">
                <MessageSquare className="w-4 h-4" />
                <span className="text-sm">Curbside Consult</span>
              </div>
            </button>
          </div>
        </div>

        {/* Tab Content */}
        {loading ? (
          <Card className="p-12 bg-white">
            <LoadingQuip size="lg" />
          </Card>
        ) : courses.length === 0 ? (
          <Card className="p-12 text-center bg-white">
            <BookOpen className="w-16 h-16 mx-auto mb-4" style={{ color: '#DADEEO' }} />
            <h3 className="text-xl font-semibold mb-2" style={{ color: '#70767C' }}>No courses available</h3>
            <p style={{ color: '#70767C' }}>Check back later for new courses</p>
          </Card>
        ) : (
          <>
            {/* Course Content */}
            {/* Courses in Progress Tab */}
            {activeTab === 'in-progress' && (() => {
              const inProgressCourses = courses.filter(c => c.status === 'in_progress');
              
              return inProgressCourses.length === 0 ? (
                <Card className="p-12 text-center bg-white">
                  <PlayCircle className="w-16 h-16 mx-auto mb-4" style={{ color: '#DADEEO' }} />
                  <h3 className="text-xl font-semibold mb-2" style={{ color: '#70767C' }}>No courses in progress</h3>
                  <p style={{ color: '#70767C' }}>Start a new course from the Available Courses tab</p>
                </Card>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {inProgressCourses.map(course => (
                      <Card 
                        key={course.id} 
                        data-testid={`continue-course-card-${course.id}`}
                        className="p-0 transition-all duration-300 bg-white overflow-hidden hover:shadow-xl cursor-pointer flex flex-col h-full"
                        style={{ border: '2px solid #102862' }}
                        onClick={() => navigate(`/course/${course.id}`)}
                      >
                        {/* Course Icon/Thumbnail with In Progress Badge */}
                        <div 
                          className="h-40 flex items-center justify-center relative" 
                          style={{ background: 'linear-gradient(135deg, #102862 0%, #1a3a7a 100%)' }}
                        >
                          {course.icon_url ? (
                            <img src={`${API}${course.icon_url}`} alt={course.title} className="w-full h-full object-cover" />
                          ) : (
                            <BookOpen className="w-16 h-16 text-white" />
                          )}
                          {/* In Progress Badge */}
                          <div className="absolute top-3 left-3 bg-green-500 px-3 py-1 rounded-full shadow-lg">
                            <span className="text-white text-xs font-bold">In Progress</span>
                          </div>
                        </div>
                        
                        {/* Course Details */}
                        <div className="p-6 flex flex-col flex-1">
                          <h3 className="text-xl font-bold mb-2 hover:underline" style={{ color: '#092036' }}>
                            {course.title}
                          </h3>
                          <p className="mb-4 text-sm line-clamp-2" style={{ color: '#70767C' }}>{course.description}</p>
                          
                          {/* Progress Info */}
                          {course.progress && (
                            <div className="mb-4 text-xs" style={{ color: '#70767C' }}>
                              <p>Questions asked: {course.progress.questions_asked}</p>
                              <p>Time spent: {course.progress.time_spent_minutes} mins</p>
                            </div>
                          )}
                          
                          {/* Course Stats */}
                          <div className="mb-4">
                            <div className="flex items-center gap-2 text-sm" style={{ color: '#092036' }}>
                              <Award className="w-4 h-4" style={{ color: '#9E3639' }} />
                              <span className="font-semibold">{course.ce_hours || 1} CE Hour{course.ce_hours !== 1 ? 's' : ''}</span>
                            </div>
                          </div>
                          
                          {/* Continue Button */}
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/course/${course.id}`);
                            }}
                            className="w-full gap-2 text-white mt-auto"
                            style={{ background: '#102862' }}
                          >
                            <PlayCircle className="w-4 h-4" />
                            Continue Course
                          </Button>
                        </div>
                  </Card>
                  ))}
                </div>
              );
            })()}

            {/* Available Courses Tab */}
            {activeTab === 'available' && (() => {
              const availableCourses = courses.filter(c => c.status === 'not_started');
              
              return availableCourses.length === 0 ? (
                <Card className="p-12 text-center bg-white">
                  <BookOpen className="w-16 h-16 mx-auto mb-4" style={{ color: '#DADEEO' }} />
                  <h3 className="text-xl font-semibold mb-2" style={{ color: '#70767C' }}>No available courses</h3>
                  <p style={{ color: '#70767C' }}>All courses have been started or completed</p>
                </Card>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {availableCourses.map(course => (
                    <Card 
                key={course.id} 
                data-testid={`student-course-card-${course.id}`}
                className={`p-0 transition-all duration-300 bg-white overflow-hidden flex flex-col h-full ${course.has_access ? 'hover:shadow-xl' : ''}`}
                style={{ border: `2px solid ${course.has_access ? '#DADEEO' : '#9E3639'}` }}
              >
                {/* Course Icon/Thumbnail */}
                <div 
                  className={`h-40 flex items-center justify-center relative ${course.has_access ? 'cursor-pointer' : ''}`} 
                  style={{ background: 'linear-gradient(135deg, #102862 0%, #1a3a7a 100%)' }}
                  onClick={() => {
                    if (course.has_access) {
                      navigate(`/course/${course.id}`);
                    }
                  }}
                >
                  {course.icon_url ? (
                    <img src={`${API}${course.icon_url}`} alt={course.title} className="w-full h-full object-cover" />
                  ) : (
                    <BookOpen className="w-16 h-16 text-white" />
                  )}
                  {/* Badge: FREE for free courses, UNLOCKED for paid with access, MEMBERS ONLY for locked */}
                  {!course.is_paid ? (
                    <div className="absolute top-3 right-3 bg-green-500 px-3 py-1 rounded-full shadow-lg">
                      <span className="font-bold text-white text-sm">FREE</span>
                    </div>
                  ) : course.has_access ? (
                    <div className="absolute top-3 right-3 px-3 py-1 rounded-full shadow-lg" style={{ background: '#dcfce7' }}>
                      <span className="font-bold text-xs" style={{ color: '#166534' }}>✓ UNLOCKED</span>
                    </div>
                  ) : (
                    <div className="absolute top-3 right-3 bg-white px-3 py-1 rounded-full shadow-lg">
                      <span className="font-bold text-xs" style={{ color: '#102862' }}>MEMBERS ONLY</span>
                    </div>
                  )}
                  {!course.has_access && (
                    <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                      <Lock className="w-12 h-12 text-white" />
                    </div>
                  )}
                </div>
                
                {/* Course Details */}
                <div className="p-6 flex flex-col flex-1">
                  <h3 
                    className={`text-xl font-bold mb-2 ${course.has_access ? 'cursor-pointer hover:underline' : ''}`} 
                    style={{ color: '#092036' }}
                    onClick={() => {
                      if (course.has_access) {
                        navigate(`/course/${course.id}`);
                      }
                    }}
                  >
                    {course.title}
                  </h3>
                  <p className="mb-4 text-sm line-clamp-2" style={{ color: '#70767C' }}>{course.description}</p>
                  
                  {/* Course Stats */}
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center gap-2 text-sm" style={{ color: '#092036' }}>
                      <Award className="w-4 h-4" style={{ color: '#9E3639' }} />
                      <span className="font-semibold">{course.ce_hours || 1} CE Hour{course.ce_hours !== 1 ? 's' : ''}</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs" style={{ color: '#70767C' }}>
                      <CheckCircle className="w-4 h-4" />
                      <span>Passing: {course.passing_grade}%</span>
                    </div>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="mt-auto">
                    {course.has_access ? (
                      <Button
                        onClick={() => navigate(`/course/${course.id}`)}
                        className="w-full"
                        style={{ background: '#102862' }}
                      >
                        Start Course
                      </Button>
                    ) : course.is_paid ? (
                      <Button
                        onClick={() => navigate('/checkout?plan=ce-membership')}
                        className="w-full gap-2"
                        style={{ background: '#102862' }}
                      >
                        Unlock with All Access Pass
                      </Button>
                    ) : (
                      <div className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold" style={{ background: '#dcfce7', color: '#15803d' }}>
                        <CheckCircle className="w-3 h-3" />
                        FREE
                      </div>
                    )}
                  </div>
                </div>
                  </Card>
                  ))}
                </div>
              );
            })()}

            {/* Completed Courses Tab */}
            {activeTab === 'completed' && (() => {
              const completedCourses = courses.filter(c => c.status === 'completed');
              
              // Calculate totals for summary strip
              const totalCourses = completedCourses.length;
              const totalCEHours = completedCourses.reduce((sum, course) => sum + (course.ce_hours || 0), 0);
              
              return completedCourses.length === 0 ? (
                <Card className="p-12 text-center bg-white">
                  <CheckCircle className="w-16 h-16 mx-auto mb-4" style={{ color: '#DADEEO' }} />
                  <h3 className="text-xl font-semibold mb-2" style={{ color: '#70767C' }}>No completed courses yet</h3>
                  <p style={{ color: '#70767C' }}>Complete a course to see it here</p>
                </Card>
              ) : (
                <>
                  {/* CE Summary Strip */}
                  <Card className="mb-6 p-4" style={{ background: '#f0fdf4', border: '1px solid #86efac' }}>
                    <div className="flex items-center gap-3">
                      <Award className="w-5 h-5 flex-shrink-0" style={{ color: '#16a34a' }} />
                      <div>
                        <p className="text-sm font-medium" style={{ color: '#092036' }}>
                          You have completed {totalCourses} {totalCourses === 1 ? 'course' : 'courses'} and earned {totalCEHours} {totalCEHours === 1 ? 'CE hour' : 'CE hours'} so far.
                        </p>
                      </div>
                    </div>
                  </Card>

                  {/* Course Cards Grid */}
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {completedCourses.map(course => {
                      // Find the certificate for this course to get completion date
                      const certificate = certificates.find(cert => cert.course_id === course.id);
                      const completionDate = certificate?.issued_at 
                        ? new Date(certificate.issued_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                        : null;

                      return (
                        <Card 
                          key={course.id} 
                          data-testid={`completed-course-card-${course.id}`}
                          className="p-0 transition-all duration-300 bg-white overflow-hidden hover:shadow-xl cursor-pointer flex flex-col h-full"
                          style={{ border: '2px solid #16a34a' }}
                          onClick={() => navigate(`/course/${course.id}`)}
                        >
                          {/* Course Icon/Thumbnail with Completed Badge */}
                          <div 
                            className="h-40 flex items-center justify-center relative" 
                            style={{ background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)' }}
                          >
                            {course.icon_url ? (
                              <img src={`${API}${course.icon_url}`} alt={course.title} className="w-full h-full object-cover" />
                            ) : (
                              <BookOpen className="w-16 h-16 text-white" />
                            )}
                            {/* Completed Badge */}
                            <div className="absolute top-3 left-3 bg-white px-3 py-1 rounded-full shadow-lg">
                              <span className="text-green-600 text-xs font-bold flex items-center gap-1">
                                <CheckCircle className="w-3 h-3" />
                                Completed
                              </span>
                            </div>
                          </div>
                          
                          {/* Course Details */}
                          <div className="p-6 flex flex-col flex-1">
                            <h3 className="text-xl font-bold mb-2 hover:underline" style={{ color: '#092036' }}>
                              {course.title}
                            </h3>
                            <p className="mb-4 text-sm line-clamp-2" style={{ color: '#70767C' }}>{course.description}</p>
                            
                            {/* Course Stats */}
                            <div className="space-y-2 mb-4">
                              <div className="flex items-center gap-2 text-sm" style={{ color: '#092036' }}>
                                <Award className="w-4 h-4" style={{ color: '#16a34a' }} />
                                <span className="font-semibold">{course.ce_hours || 1} CE Hour{course.ce_hours !== 1 ? 's' : ''} Earned</span>
                              </div>
                              {completionDate && (
                                <div className="flex items-center gap-2 text-xs" style={{ color: '#70767C' }}>
                                  <CheckCircle className="w-4 h-4" />
                                  <span>Completed on {completionDate}</span>
                                </div>
                              )}
                            </div>

                            {/* Download Certificate Button */}
                            <div className="mt-auto">
                            <Button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewCertificate(course.id);
                              }}
                              variant="outline"
                              className="w-full gap-2 mb-2 font-semibold"
                              style={{ borderColor: '#f59e0b', color: '#92400e', background: '#fffbeb' }}
                              data-testid={`download-cert-btn-${course.id}`}
                            >
                              <Download className="w-4 h-4" />
                              Download Certificate
                            </Button>
                            
                            {/* Review Button */}
                            <Button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/course/${course.id}`);
                              }}
                              className="w-full gap-2 text-white"
                              style={{ background: '#16a34a' }}
                            >
                              <BookOpen className="w-4 h-4" />
                              Review course material
                            </Button>
                            </div>
                          </div>
                        </Card>
                      );
                    })}
                  </div>
                </>
              );
            })()}

            {/* CE Certificates Tab */}
            {activeTab === 'certificates' && (() => {
              if (certificates.length === 0) {
                return (
                  <Card className="p-12 text-center bg-white">
                    <Award className="w-16 h-16 mx-auto mb-4" style={{ color: '#DADEEO' }} />
                    <h3 className="text-xl font-semibold mb-2" style={{ color: '#70767C' }}>No CE certificates yet</h3>
                    <p style={{ color: '#70767C' }}>Complete a course to earn your certificate</p>
                  </Card>
                );
              }

              // Get unique years from certificates
              const years = [...new Set(certificates.map(cert => 
                new Date(cert.issued_at).getFullYear()
              ))].sort((a, b) => b - a);

              // Filter certificates by year
              let filteredCerts = certificates;
              if (certificateYearFilter !== 'all') {
                filteredCerts = certificates.filter(cert => 
                  new Date(cert.issued_at).getFullYear().toString() === certificateYearFilter
                );
              }

              // Sort certificates
              const sortedCerts = [...filteredCerts].sort((a, b) => {
                const dateA = new Date(a.issued_at);
                const dateB = new Date(b.issued_at);
                
                if (certificateSortBy === 'newest') {
                  return dateB - dateA;
                } else if (certificateSortBy === 'oldest') {
                  return dateA - dateB;
                } else { // course name
                  return a.course_title.localeCompare(b.course_title);
                }
              });

              // Calculate total CE hours for filtered certificates
              const totalCEHours = sortedCerts
                .reduce((sum, cert) => {
                  const course = courses.find(c => c.id === cert.course_id);
                  return sum + (course?.ce_hours || 0);
                }, 0);

              return (
                <>
                  {/* CE Hours Summary */}
                  <Card className="mb-6 p-4" style={{ background: '#f9fafb', border: '1px solid #e5e7eb' }}>
                    <div className="flex items-center gap-3">
                      <Award className="w-6 h-6 flex-shrink-0" style={{ color: '#102862' }} />
                      <div>
                        <p className="font-semibold text-lg" style={{ color: '#092036' }}>
                          Total CE hours earned on this platform: {totalCEHours}
                        </p>
                        {certificateYearFilter !== 'all' && (
                          <p className="text-sm mt-1" style={{ color: '#70767C' }}>
                            Showing certificates from {certificateYearFilter}
                          </p>
                        )}
                      </div>
                    </div>
                  </Card>

                  {/* Filters and Export Controls */}
                  <div className="flex justify-between items-center mb-4 gap-4 flex-wrap">
                    {/* Left side: Filters */}
                    <div className="flex gap-4 items-center">
                      {/* Year Filter */}
                      <div className="flex items-center gap-2">
                        <label className="text-sm font-medium" style={{ color: '#70767C' }}>Show:</label>
                        <select
                          value={certificateYearFilter}
                          onChange={(e) => setCertificateYearFilter(e.target.value)}
                          className="px-3 py-2 text-sm border rounded"
                          style={{ borderColor: '#DADEEO', color: '#092036' }}
                        >
                          <option value="all">All years</option>
                          {years.map(year => (
                            <option key={year} value={year.toString()}>{year}</option>
                          ))}
                        </select>
                      </div>

                      {/* Sort Filter */}
                      <div className="flex items-center gap-2">
                        <label className="text-sm font-medium" style={{ color: '#70767C' }}>Sort by:</label>
                        <select
                          value={certificateSortBy}
                          onChange={(e) => setCertificateSortBy(e.target.value)}
                          className="px-3 py-2 text-sm border rounded"
                          style={{ borderColor: '#DADEEO', color: '#092036' }}
                        >
                          <option value="newest">Newest first</option>
                          <option value="oldest">Oldest first</option>
                          <option value="course">Course name</option>
                        </select>
                      </div>
                    </div>

                    {/* Right side: Export buttons */}
                    <div className="flex gap-2">
                      <Button
                        onClick={() => generatePDFReport(sortedCerts)}
                        className="gap-2 text-white"
                        style={{ background: '#102862' }}
                      >
                        <Download className="w-4 h-4" />
                        Download CE report (PDF)
                      </Button>
                    </div>
                  </div>

                  {/* Certificates Table */}
                  <Card className="overflow-hidden" style={{ backgroundColor: colors.tableBg, border: `2px solid ${colors.border}` }}>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b" style={{ backgroundColor: colors.tableHeaderBg, borderColor: colors.border }}>
                            <th className="text-left p-4 font-semibold" style={{ color: colors.heading }}>Course</th>
                            <th className="text-left p-4 font-semibold" style={{ color: colors.heading }}>CE Hours</th>
                            <th className="text-left p-4 font-semibold" style={{ color: colors.heading }}>Completion Date</th>
                            <th className="text-right p-4 font-semibold" style={{ color: colors.heading }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sortedCerts
                            .map(cert => {
                            const course = courses.find(c => c.id === cert.course_id);
                            const ceHours = course?.ce_hours || 0;

                            return (
                              <tr key={cert.id} className="border-b last:border-b-0 transition-colors" style={{ borderColor: colors.border }}>
                                <td className="p-4">
                                  <div className="flex items-center gap-3">
                                    <Award className="w-5 h-5 flex-shrink-0" style={{ color: '#9E3639' }} />
                                    <span className="font-medium" style={{ color: colors.heading }}>{cert.course_title}</span>
                                  </div>
                                </td>
                                <td className="p-4">
                                  <span className="font-semibold" style={{ color: colors.heading }}>{ceHours}</span>
                                </td>
                                <td className="p-4" style={{ color: colors.subtext }}>
                                  {new Date(cert.issued_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                </td>
                                <td className="p-4">
                                  <div className="flex justify-end gap-2">
                                    <Button
                                      onClick={() => handleViewCertificate(cert.course_id)}
                                      data-testid={`view-certificate-${cert.course_id}`}
                                      className="gap-1"
                                      variant="outline"
                                      size="sm"
                                      style={{ borderColor: isDarkMode ? '#60a5fa' : '#102862', color: isDarkMode ? '#60a5fa' : '#102862' }}
                                    >
                                      <Eye className="w-4 h-4" />
                                      View letter
                                    </Button>
                                    <Button
                                      onClick={() => handleDownloadCertificate(cert.course_id, cert.course_title)}
                                      className="gap-1 bg-green-600 hover:bg-green-700 text-white"
                                      size="sm"
                                    >
                                      <Download className="w-4 h-4" />
                                      Download PDF
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </Card>
                </>
              );
            })()}

            {/* Curbside Consult Tab */}
            {activeTab === 'curbside-consult' && (
              <Card className="p-12 text-center" style={{ backgroundColor: colors.cardBg }}>
                <MessageSquare className="w-16 h-16 mx-auto mb-4" style={{ color: '#f97316' }} />
                <h3 className="text-2xl font-bold mb-4" style={{ color: colors.heading }}>
                  Curbside Consult
                </h3>
                
                {hasCurbsideAccess ? (
                  <>
                    <p className="mb-6 text-lg" style={{ color: colors.subtext }}>
                      Get expert advice from our instructors. Ask questions, browse answered Q&A, and learn from the community.
                    </p>
                    <Button
                      onClick={() => {
                        const params = new URLSearchParams(window.location.search);
                        const questionId = params.get('question');
                        if (questionId) {
                          navigate(`/curbside-consult?question=${questionId}`);
                        } else {
                          navigate('/curbside-consult');
                        }
                      }}
                      className="gap-2 text-white text-lg px-8 py-6"
                      style={{ background: '#f97316' }}
                    >
                      <MessageSquare className="w-5 h-5" />
                      Go to Curbside Consult
                    </Button>
                  </>
                ) : (
                  <>
                    <div className="mb-6 p-4 rounded-lg" style={{ backgroundColor: isDarkMode ? '#1e3a5f' : '#FFF7ED' }}>
                      <Lock className="w-8 h-8 mx-auto mb-3" style={{ color: '#f97316' }} />
                      <p className="text-lg font-medium mb-2" style={{ color: colors.heading }}>
                        Upgrade to Access Curbside Consult
                      </p>
                      <p className="text-sm" style={{ color: colors.subtext }}>
                        Get direct feedback from a board-certified oral surgeon on your real-world cases.
                      </p>
                    </div>
                    
                    <div className="mb-6 text-left max-w-md mx-auto space-y-2">
                      <p className="flex items-center gap-2 text-sm" style={{ color: colors.subtext }}>
                        <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                        2 case questions per month
                      </p>
                      <p className="flex items-center gap-2 text-sm" style={{ color: colors.subtext }}>
                        <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                        1 follow-up per case
                      </p>
                      <p className="flex items-center gap-2 text-sm" style={{ color: colors.subtext }}>
                        <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                        3-5 business day response time
                      </p>
                      <p className="flex items-center gap-2 text-sm" style={{ color: colors.subtext }}>
                        <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                        Text, video, audio, or annotated image responses
                      </p>
                      <p className="flex items-center gap-2 text-sm" style={{ color: colors.subtext }}>
                        <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                        Includes full course library + Practice Resources
                      </p>
                    </div>
                    
                    <div className="mb-4">
                      <span className="text-3xl font-bold" style={{ color: colors.heading }}>$150</span>
                      <span className="text-lg" style={{ color: colors.subtext }}>/case</span>
                    </div>
                    
                    <Button
                      onClick={() => navigate('/checkout?plan=curbside-consult&billing=monthly')}
                      className="gap-2 text-white text-lg px-8 py-6"
                      style={{ background: '#f97316' }}
                      data-testid="upgrade-curbside-btn"
                    >
                      <CreditCard className="w-5 h-5" />
                      Upgrade to Curbside Consult
                    </Button>
                  </>
                )}
              </Card>
            )}

            {/* Practice Resources Tab */}
            {activeTab === 'practice-resources' && (
              <Card className="p-12 text-center bg-white">
                <FolderOpen className="w-16 h-16 mx-auto mb-4" style={{ color: '#16a34a' }} />
                <h3 className="text-2xl font-bold mb-4" style={{ color: '#092036' }}>
                  Practice Resources
                </h3>
                <p className="mb-6 text-lg" style={{ color: '#70767C' }}>
                  Access curated educational materials including PDFs, videos, audio files, and more. Browse by category, filter by tags, and bookmark your favorites.
                </p>
                <Button
                  onClick={() => navigate('/practice-resources')}
                  className="gap-2 text-white text-lg px-8 py-6"
                  style={{ background: '#16a34a' }}
                >
                  <FolderOpen className="w-5 h-5" />
                  Browse Resources
                </Button>
              </Card>
            )}
          </>
        )}
      </main>

      {/* Certificate Viewer Modal */}
      <Dialog open={!!viewingCertificate} onOpenChange={handleCloseCertificate}>
        <DialogContent className="max-w-4xl w-full h-[90vh] p-0">
          <div className="relative w-full h-full flex flex-col" style={{ background: isDarkMode ? '#1a2e44' : '#f3f4f6' }}>
            {/* Header bar with close and download */}
            <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: isDarkMode ? '#1e3a5f' : '#e5e7eb' }}>
              <span className="font-semibold text-sm" style={{ color: isDarkMode ? '#ffffff' : '#092036' }}>
                Course Completion Certificate
              </span>
              <div className="flex items-center gap-2">
                {certificatePdfUrl && (
                  <button
                    onClick={() => handleDownloadCertificate(viewingCertificate, 'Certificate')}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-md text-white"
                    style={{ background: '#16a34a' }}
                  >
                    <Download className="w-4 h-4" />
                    Download
                  </button>
                )}
                <button
                  onClick={handleCloseCertificate}
                  className="rounded-full w-8 h-8 flex items-center justify-center transition-colors hover:bg-gray-200"
                  aria-label="Close"
                >
                  <X className="w-5 h-5" style={{ color: isDarkMode ? '#ffffff' : '#092036' }} />
                </button>
              </div>
            </div>
            
            {/* PDF Canvas Viewer */}
            <div className="flex-1 min-h-0 overflow-auto p-4 flex justify-center">
              {loadingCertificate ? (
                <div className="w-full h-full flex items-center justify-center">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={{ borderColor: '#102862' }}></div>
                    <p style={{ color: '#70767C' }}>Loading certificate...</p>
                  </div>
                </div>
              ) : certificatePdfUrl ? (
                <CertificatePdfViewer fileData={certificatePdfUrl} />
              ) : null}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Profile Modal */}
      <Dialog open={showProfileModal} onOpenChange={setShowProfileModal}>
        <DialogContent className="max-w-md">
          <div className="p-6">
            <div className="flex items-center gap-3 mb-6">
              <User className="w-8 h-8 text-blue-600" />
              <div>
                <h2 className="text-xl font-bold" style={{ color: isDarkMode ? '#ffffff' : '#092036' }}>
                  Update Profile
                </h2>
                <p className="text-sm" style={{ color: colors.subtext }}>
                  Required for CE certificates
                </p>
              </div>
            </div>
            
            {needsProfileUpdate && (
              <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                <p className="text-sm text-orange-800">
                  ⚠️ Please add your State and License Number. This information is required on your CE certificates.
                </p>
              </div>
            )}
            
            <div className="space-y-4">
              <div>
                <Label htmlFor="profile-state" style={{ color: isDarkMode ? '#ffffff' : '#092036' }}>
                  State <span className="text-red-500">*</span>
                </Label>
                <select
                  id="profile-state"
                  value={profileForm.state}
                  onChange={(e) => setProfileForm({ ...profileForm, state: e.target.value })}
                  className="flex h-10 w-full rounded-md border px-3 py-2 text-sm mt-1"
                  style={{ 
                    backgroundColor: isDarkMode ? '#1e3a5f' : '#ffffff', 
                    color: isDarkMode ? '#ffffff' : '#092036', 
                    borderColor: isDarkMode ? '#1e3a5f' : '#DADEEO' 
                  }}
                >
                  <option value="">Select State</option>
                  {US_STATES.map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <Label htmlFor="profile-license" style={{ color: isDarkMode ? '#ffffff' : '#092036' }}>
                  License Number <span className="text-red-500">*</span>
                </Label>
                <input
                  id="profile-license"
                  type="text"
                  placeholder="e.g., 12345"
                  value={profileForm.license_number}
                  onChange={(e) => setProfileForm({ ...profileForm, license_number: e.target.value })}
                  className="flex h-10 w-full rounded-md border px-3 py-2 text-sm mt-1"
                  style={{ 
                    backgroundColor: isDarkMode ? '#1e3a5f' : '#ffffff', 
                    color: isDarkMode ? '#ffffff' : '#092036', 
                    borderColor: isDarkMode ? '#1e3a5f' : '#DADEEO' 
                  }}
                />
              </div>
              <div>
                <Label htmlFor="profile-agd" style={{ color: isDarkMode ? '#ffffff' : '#092036' }}>
                  AGD Member Number <span className="text-xs opacity-70">(optional)</span>
                </Label>
                <input
                  id="profile-agd"
                  type="text"
                  placeholder="Leave blank if not an AGD member"
                  value={profileForm.agd_number}
                  onChange={(e) => setProfileForm({ ...profileForm, agd_number: e.target.value })}
                  data-testid="profile-agd-input"
                  className="flex h-10 w-full rounded-md border px-3 py-2 text-sm mt-1"
                  style={{ 
                    backgroundColor: isDarkMode ? '#1e3a5f' : '#ffffff', 
                    color: isDarkMode ? '#ffffff' : '#092036', 
                    borderColor: isDarkMode ? '#1e3a5f' : '#DADEEO' 
                  }}
                />
              </div>
              
              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setShowProfileModal(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveProfile}
                  disabled={savingProfile || !profileForm.state || !profileForm.license_number}
                  className="flex-1 text-white"
                  style={{ backgroundColor: '#102862' }}
                >
                  {savingProfile ? (
                    <>
                      <LoadingQuip size="sm" />
                      <span className="ml-2">Saving...</span>
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Profile
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}